// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - TOOLBAR GRAFCET
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/grafcet/components/GrafcetToolbar.jsx
// Barre d'outils pour l'éditeur GRAFCET
// Papy 73+ - Joshua approved! 🚀
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import '../styles/GrafcetToolbar.css';

// ═══════════════════════════════════════════════════════════════════════════
// MODAL NOUVEAU SCÉNARIO
// ═══════════════════════════════════════════════════════════════════════════

function NewScenarioModal({ isOpen, onClose, onCreate, sousDevisions }) {
  const [nom, setNom] = useState('');
  const [sousDevisionId, setSousDevisionId] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nom || !sousDevisionId) return;
    onCreate(nom, parseInt(sousDevisionId), description);
    setNom('');
    setSousDevisionId('');
    setDescription('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="grafcet-modal-overlay" onClick={onClose}>
      <div className="grafcet-modal" onClick={(e) => e.stopPropagation()}>
        <div className="grafcet-modal__header">
          <h3>📋 Nouveau Scénario GRAFCET</h3>
          <button className="grafcet-modal__close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="grafcet-modal__body">
          <div className="grafcet-form-group">
            <label>Nom du scénario *</label>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex: Arrosage Serre Nord"
              required
            />
          </div>
          <div className="grafcet-form-group">
            <label>Parcelle / Zone *</label>
            <select
              value={sousDevisionId}
              onChange={(e) => setSousDevisionId(e.target.value)}
              required
            >
              <option value="">Sélectionner...</option>
              {sousDevisions.map((sd) => (
                <option key={sd.id} value={sd.id}>
                  {sd.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="grafcet-form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description optionnelle..."
              rows={3}
            />
          </div>
          <div className="grafcet-modal__footer">
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn btn--primary">
              ✨ Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL TOOLBAR
// ═══════════════════════════════════════════════════════════════════════════

export default function GrafcetToolbar({
  scenario,
  scenarios,
  onSelectScenario,
  onNewScenario,
  onSave,
  onRun,
  onStop,
  isSaving,
  hasChanges,
  sousDevisions
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  const handleSelectChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    if (id) {
      onSelectScenario(parseInt(id));
    }
  };

  const getStatusBadge = () => {
    if (!scenario) return null;
    
    const statusConfig = {
      'arrete': { label: 'Arrêté', class: 'status--stopped', icon: '⏹️' },
      'en_cours': { label: 'En cours', class: 'status--running', icon: '▶️' },
      'pause': { label: 'Pause', class: 'status--paused', icon: '⏸️' },
      'erreur': { label: 'Erreur', class: 'status--error', icon: '❌' }
    };
    
    const status = statusConfig[scenario.statut_execution] || statusConfig['arrete'];
    
    return (
      <span className={`grafcet-toolbar__status ${status.class}`}>
        {status.icon} {status.label}
      </span>
    );
  };

  return (
    <>
      <div className="grafcet-toolbar">
        {/* Logo et titre */}
        <div className="grafcet-toolbar__brand">
          <span className="grafcet-toolbar__logo">⚙️</span>
          <span className="grafcet-toolbar__title">Éditeur GRAFCET</span>
        </div>
        
        {/* Sélecteur de scénario */}
        <div className="grafcet-toolbar__selector">
          <select
            value={selectedId}
            onChange={handleSelectChange}
            className="grafcet-toolbar__select"
          >
            <option value="">-- Sélectionner un scénario --</option>
            {scenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nom} ({s.sous_division_nom || 'Zone ?'})
              </option>
            ))}
          </select>
          
          <button
            className="grafcet-toolbar__btn grafcet-toolbar__btn--new"
            onClick={() => setIsModalOpen(true)}
            title="Nouveau scénario"
          >
            ➕ Nouveau
          </button>
        </div>
        
        {/* Info scénario actif */}
        {scenario && (
          <div className="grafcet-toolbar__info">
            <span className="grafcet-toolbar__scenario-name">{scenario.nom}</span>
            {getStatusBadge()}
            <span className="grafcet-toolbar__version">v{scenario.version}</span>
            {hasChanges && (
              <span className="grafcet-toolbar__unsaved">● Non sauvegardé</span>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="grafcet-toolbar__actions">
          <button
            className="grafcet-toolbar__btn grafcet-toolbar__btn--save"
            onClick={onSave}
            disabled={!scenario || isSaving || !hasChanges}
            title="Sauvegarder"
          >
            {isSaving ? '⏳' : '💾'} {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
          
          {scenario?.statut_execution === 'en_cours' ? (
            <button
              className="grafcet-toolbar__btn grafcet-toolbar__btn--stop"
              onClick={onStop}
              title="Arrêter"
            >
              ⏹️ Arrêter
            </button>
          ) : (
            <button
              className="grafcet-toolbar__btn grafcet-toolbar__btn--run"
              onClick={onRun}
              disabled={!scenario || hasChanges}
              title={hasChanges ? 'Sauvegardez d\'abord' : 'Démarrer'}
            >
              ▶️ Démarrer
            </button>
          )}
        </div>
      </div>
      
      {/* Modal nouveau scénario */}
      <NewScenarioModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={onNewScenario}
        sousDevisions={sousDevisions}
      />
    </>
  );
}
